import { callLambdaApi } from "../callLambdaApi";
import { ApiNames, Methods } from "../constants";

/**
 * MLModelApiCommunicator
 * 
 * Handles all API communication for ML model-related operations.
 * Provides methods for CRUD operations on ML models including training,
 * result management, and prediction handling.
 * 
 * This communicator follows the same pattern as other API communicators
 * in the application and uses the centralized callLambdaApi function.
 */
export class MLModelApiCommunicator {
  /**
   * @param {object} requestBody
   * @param {string} requestBody.modelName
   */
  create(
    requestBody = {
      mlModelName: "New ML Model",
      friendlyName: "",
      description: "",
      printerId: "",
      materialId: "",
      datasetIds: [],
    }
  ) {
    return callLambdaApi({
      method: Methods.POST,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL(),
      payload: {
        body: {
          ...requestBody,
        },
      },
    });
  }

  /**
   *
   * @param {string} id
   * @returns {Promise<{
   * data: {
   * datasetId: string;
   * datasetName: string;
   * datasetType: string;
   * dateAdded: number;
   * modelIds: string[];
   * models: object[];
   * orgId: string;
   * userId: string;
   * printer: string;
   * material: string;
   * }
   * }>}
   */
  fetch(id) {
    console.log("fetch()");
    return callLambdaApi({
      method: Methods.GET,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${id}`,
    });
  }

  fetchResult(id, resultId) {
    console.log("fetch()");
    return callLambdaApi({
      method: Methods.GET,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${id}/result/${resultId}`,
    });
  }

  /**  @returns {Promise<import('api').GETResponse<import("models").MLModel[]>} */
  list() {
    console.log("list()");
    return callLambdaApi({
      method: Methods.GET,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL(),
    });
  }

  listPrinters() {
    return callLambdaApi({
      method: Methods.GET,
      apiName: ApiNames.Submissions,
      path: '/printers',
    });
  }

  listMaterials() {
    return callLambdaApi({
      method: Methods.GET,
      apiName: ApiNames.Submissions,
      path: '/materials',
    });
  }

  update(requestBody) {
    console.log("update()", requestBody);
    return callLambdaApi({
      method: Methods.PUT,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${requestBody.mlModelId}`,
      payload: { body: requestBody },
    });
  }

  generateMLModel(mlModelId, options) {
    return callLambdaApi({
      method: Methods.POST,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/process`,
      payload: { body: {'mlModelId': mlModelId, 'options': options} },
    });
  }

  delete(id) {
    return callLambdaApi({
      method: Methods.DELETE,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${id}`,
    });
  }

  deletePrediction(mlModelId, resultId, predictionId) {
    return callLambdaApi({
      method: Methods.DELETE,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${mlModelId}/result/${resultId}/prediction/${predictionId}`,
    });
  }

  publishResult(mlModelId, resultId ) {
    return callLambdaApi({
      method: Methods.POST,
      apiName: ApiNames.Submissions,
      path: this._buildBaseURL() + `/${mlModelId}/result/${resultId}/publish`,
    });
  }

  /**
   * Returns the default serverless api endpoint
   * using the instance's project and model ids.
   * @private
   */
  _buildBaseURL() {
    return `/mlmodel`;
  }
}
